import React, { useState, useEffect } from 'react';
import './Quality.css';

const Quality = () => {
  const [peanutPosition, setPeanutPosition] = useState(0);

  const processingSteps = [
    {
      id: 1,
      title: 'Raw Material Storage',
      description: 'At S. V. Gadag Ginning Factory, we prioritize the quality and freshness of our raw materials. Our state-of-the-art storage facility ensures optimal conditions for storing the agricultural products we export. With controlled temperature and humidity, we maintain the integrity of the raw materials, preserving their taste, aroma, and nutritional value.',
      icon: 'fa-warehouse'
    },
    {
      id: 2,
      title: 'Pre-Cleaning',
      description: 'Before processing our agricultural products, we employ a thorough pre-cleaning process. This step involves removing any impurities, such as dirt, stones, or foreign matter, from the raw materials. Our advanced cleaning machinery efficiently separates unwanted particles, ensuring that only the highest quality produce moves forward.',
      icon: 'fa-broom'
    },
    {
      id: 3,
      title: 'De-Shelling',
      description: 'As part of our production process, we perform de-shelling of peanuts to remove the outer shell or husk from the raw peanuts. This step is crucial in preparing the peanuts for further processing and packaging. The de-shelling process ensures that only the inner kernel is used in our agricultural products.',
      icon: 'fa-cog'
    },
    {
      id: 4,
      title: 'De-Stoner',
      description: 'To maintain the purity and quality of our products, we employ a de-stoner machine in our production line. This equipment effectively removes stones, pebbles, and other heavy impurities from the raw materials. By eliminating these unwanted elements, we ensure that our agricultural products meet the highest standards of cleanliness and safety.',
      icon: 'fa-filter'
    },
    {
      id: 5,
      title: 'Gravity Seeds Separator',
      description: 'We utilize a gravity seeds separator to further enhance the quality of our agricultural products. This specialized machine employs the principles of gravity to separate the raw materials from lighter and immature seeds. By carefully controlling the airflow and vibration, we achieve optimal separation.',
      icon: 'fa-balance-scale'
    },
    {
      id: 6,
      title: 'Sorting',
      description: 'Precision sorting is a crucial step in our production process. Our advanced sorting machines utilize cutting-edge technology to analyze the color, size, and quality of the agricultural products. By automatically detecting and removing any defective or substandard items, we ensure that only the finest produce is selected.',
      icon: 'fa-sort'
    },
    {
      id: 7,
      title: 'Uniform Grading',
      description: 'To meet the diverse requirements of our customers, we employ a uniform grading process for our agricultural products. This involves sorting and categorizing the produce into different grades based on specific quality parameters. By maintaining consistent grading standards, we can provide our customers with a range of options.',
      icon: 'fa-layer-group'
    },
    {
      id: 8,
      title: 'Metal Separation',
      description: 'We prioritize food safety, and that includes ensuring that our agricultural products are free from metal contaminants. We utilize advanced metal separation technology during the production process. This specialized equipment effectively detects and removes any metal particles that may be present in the raw materials.',
      icon: 'fa-magnet'
    },
    {
      id: 9,
      title: 'Pre-Shipment Lab Analysis',
      description: 'To ensure the highest level of quality control, we conduct comprehensive pre-shipment lab analyses on our agricultural products. Our dedicated team of experts performs rigorous testing, including physical, chemical, and microbiological analysis, to verify the quality, safety, and compliance of the produce.',
      icon: 'fa-flask'
    },
    {
      id: 10,
      title: 'Packaging',
      description: 'We understand the importance of packaging in preserving the freshness, quality, and appeal of our agricultural products. Our customer-oriented packaging approach ensures that our exports are carefully packaged to meet the specific requirements and preferences of our clients.',
      icon: 'fa-box'
    },
    {
      id: 11,
      title: 'Sterilization',
      description: 'To maintain the highest standards of hygiene and safety, we employ advanced sterilization techniques in our production process. Sterilization is a critical step that eliminates any harmful microorganisms, bacteria, or pathogens that may be present in the agricultural products.',
      icon: 'fa-shield-alt'
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const timeline = document.querySelector('.processing-timeline');
      
      if (!timeline) return;
      
      const timelineRect = timeline.getBoundingClientRect();
      const timelineTop = timelineRect.top;
      const timelineHeight = timelineRect.height;
      const windowHeight = window.innerHeight;
      
      // Calculate smooth peanut position (0 to 1)
      let position = 0;
      
      if (timelineTop <= windowHeight && timelineTop + timelineHeight >= 0) {
        // Timeline is in view - calculate smooth progress
        const scrollStart = timelineTop;
        const scrollEnd = timelineTop + timelineHeight;
        const viewportCenter = windowHeight / 2;
        
        // Calculate how far through the timeline we've scrolled
        const scrollProgress = Math.max(0, Math.min(1, (viewportCenter - scrollStart) / (scrollEnd - scrollStart)));
        position = scrollProgress;
      } else if (timelineTop > windowHeight) {
        // Timeline not yet in view - keep at top
        position = 0;
      } else {
        // Timeline has passed - keep at bottom
        position = 1;
      }
      
      setPeanutPosition(position);
    };

    // Use requestAnimationFrame for smoother scrolling
    let ticking = false;
    const smoothScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', smoothScroll, { passive: true });
    handleScroll(); // Check initial state
    
    return () => window.removeEventListener('scroll', smoothScroll);
  }, []);

  const certifications = [
    {
      name: 'ISO 22000:2005',
      description: 'Food Safety Management System',
      logo: '/assets/logos/ISO-logo.jpg',
      details: 'International standard for food safety management systems, ensuring safe food production and handling processes.'
    },
    {
      name: 'HACCP',
      description: 'Hazard Analysis Critical Control Points',
      logo: '/assets/logos/HACCP-ico.jpg',
      details: 'Systematic preventive approach to food safety that identifies physical, chemical, and biological hazards.'
    },
    {
      name: '3 Star ⭐⭐⭐ Export House',
      description: 'Government of India Recognition',
      logo: '/assets/logos/STAR-EXPORT-HOUSE-logo.jpg',
      details: 'Recognized by the Government of India as a 3 Star Export House for outstanding export performance.'
    },
    {
      name: 'FSSAI',
      description: 'Food Safety and Standards Authority of India',
      logo: '/assets/logos/FSSAI-ico.jpg',
      details: 'Compliance with Indian food safety and standards regulations for food products.'
    },
    {
      name: 'APEDA',
      description: 'Agricultural and Processed Food Products Export Development Authority',
      logo: '/assets/logos/APEDA-ico.jpg',
      details: 'Registered with APEDA for export of agricultural and processed food products.'
    },
    {
      name: 'MSME',
      description: 'Micro, Small and Medium Enterprises',
      logo: '/assets/logos/MSME-ico.jpg',
      details: 'Registered under the Micro, Small and Medium Enterprises Development Act.'
    },
    {
      name: 'IOPEPC',
      description: 'Indian Oilseeds and Produce Export Promotion Council',
      logo: '/assets/logos/IOPEPC-ico.jpg',
      details: 'Member of IOPEPC for promotion and development of oilseeds and produce exports.'
    },
    {
      name: 'KSSAI',
      description: 'Karnataka State Food Safety Authority',
      logo: '/assets/logos/KSSAI-ico.jpg',
      details: 'Compliance with Karnataka State food safety regulations and standards.'
    },
    {
      name: 'FIEO',
      description: 'Federation of Indian Export Organisations',
      logo: '/assets/logos/FIEO-logo.jpg',
      details: 'Member of FIEO, the apex body of export promotion councils in India.'
    }
  ];

  const qualityStandards = [
    {
      title: 'Raw Material Quality',
      description: 'We source only the finest quality raw materials from trusted suppliers and conduct rigorous quality checks before processing.',
      icon: 'fa-seedling'
    },
    {
      title: 'Processing Standards',
      description: 'Our state-of-the-art processing facilities ensure that all products meet international quality standards and food safety requirements.',
      icon: 'fa-cogs'
    },
    {
      title: 'Testing & Analysis',
      description: 'Comprehensive testing for moisture content, aflatoxin levels, foreign matter, and other quality parameters is conducted in certified laboratories.',
      icon: 'fa-flask'
    },
    {
      title: 'Packaging & Storage',
      description: 'Products are packed in food-grade materials and stored under controlled conditions to maintain quality and freshness.',
      icon: 'fa-box'
    },
    {
      title: 'Traceability',
      description: 'Complete traceability from raw material sourcing to final product delivery ensures quality control at every step.',
      icon: 'fa-search'
    },
    {
      title: 'Continuous Improvement',
      description: 'Regular audits, training programs, and process improvements ensure that our quality standards are continuously enhanced.',
      icon: 'fa-chart-line'
    }
  ];

  return (
    <div className="quality-page">
      {/* Hero Section */}
      <section className="quality-hero">
        <div className="hero-background"></div>
        <div className="hero-gradient"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Quality <span>Standards</span></h1>
              <p className="lead">
                Committed to delivering the highest quality products through rigorous quality control, 
                international certifications, and continuous improvement processes.
              </p>
            </div>
            <div className="hero-visual">
              <div className="quality-badges">
                <div className="badge-item">
                  <i className="fa fa-certificate"></i>
                  <span>ISO 22000:2005</span>
                </div>
                <div className="badge-item">
                  <i className="fa fa-shield"></i>
                  <span>HACCP Certified</span>
                </div>
                <div className="badge-item">
                  <i className="fa fa-star"></i>
                  <span>3 Star <i className="fa fa-star" style={{color: '#f39c12'}}></i><i className="fa fa-star" style={{color: '#f39c12'}}></i><i className="fa fa-star" style={{color: '#f39c12'}}></i> Export House</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section certifications">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="section-header text-center">
                <h2>Certifications & Recognitions</h2>
                <p className="lead">
                  We are proud to be certified and recognized by various national and international 
                  organizations for our commitment to quality and food safety.
                </p>
              </div>
            </div>
          </div>
          <div className="row">
            {certifications.map((cert, index) => (
              <div key={index} className="col-md-6 col-lg-4 mb-4">
                <div className="certification-card">
                  <div className="cert-logo">
                    <img src={cert.logo} alt={cert.name} className="img-responsive" />
                  </div>
                  <div className="cert-content">
                    <h4>{cert.name}</h4>
                    <p className="cert-description">{cert.description}</p>
                    <p className="cert-details">{cert.details}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Peanut Processing Steps */}
      <section id="processing-steps" className="section processing-steps">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <div className="section-header text-center">
                <h2>Peanut Processing Journey</h2>
                <p className="lead">
                  Crafting Excellence: Nurturing the Finest Peanuts through Impeccable Processing
                </p>
              </div>
            </div>
          </div>
          
          <div className="processing-timeline">
            <div className="timeline-line">
              <div 
                className="peanut-icon"
                style={{ 
                  top: `${peanutPosition * 100}%`
                }}
              >
                <i className="fa fa-seedling"></i>
              </div>
            </div>
            
            <div className="steps-container">
              {processingSteps.map((step, index) => (
                <div 
                  key={step.id} 
                  className={`process-step visible ${index % 2 === 0 ? 'left' : 'right'}`}
                  style={{ '--step-delay': `${index * 0.05}s` }}
                >
                  {index % 2 === 0 ? (
                    // Left side
                    <>
                      <div className="step-content">
                        <div className="step-details">
                          <h4>{step.title}</h4>
                          <p>{step.description}</p>
                        </div>
                      </div>
                      <div className="step-number">
                        <i className={`fa ${step.icon}`}></i>
                      </div>
                    </>
                  ) : (
                    // Right side
                    <>
                      <div className="step-number">
                        <i className={`fa ${step.icon}`}></i>
                      </div>
                      <div className="step-content">
                        <div className="step-details">
                          <h4>{step.title}</h4>
                          <p>{step.description}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Metrics */}
      <section className="section quality-metrics">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <h2 className="text-center mb-5">Quality Metrics</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-md-3">
              <div className="metric-card">
                <div className="metric-number">26+</div>
                <div className="metric-label">Years of Experience</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="metric-card">
                <div className="metric-number">120+</div>
                <div className="metric-label">International Importers</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="metric-card">
                <div className="metric-number">15</div>
                <div className="metric-label">Quality Certifications</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="metric-card">
                <div className="metric-number">100%</div>
                <div className="metric-label">Quality Commitment</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Quality */}
      <section className="section contact-quality">
        <div className="container">
          <div className="row">
            <div className="col-md-8">
              <div className="contact-content">
                <h2>Quality Inquiries</h2>
                <p style={{ color: 'black' }}>
                  For detailed information about our quality standards, certifications, or to request 
                  quality certificates for our products, please contact our quality assurance team.
                </p>
                <div className="contact-info">
                  <div className="contact-item whatsapp-contact">
                    <svg className="whatsapp-icon" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                    </svg>
                    <a href="https://wa.me/919448165266?text=Hello! I'm interested in your peanut export services. Could you please provide more information?" target="_blank" rel="noopener noreferrer">+91 944 816 5266</a>
                  </div>
                  <div className="contact-item">
                    <i className="fa fa-envelope"></i>
                    <a href="mailto:svgadag_12@yahoo.com">svgadag_12@yahoo.com</a>
                  </div>
                  <div className="contact-item">
                    <i className="fa fa-phone"></i>
                    <a href="tel:+919448165266">+91 944 816 5266</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="contact-cta">
                <h3 style={{ color: 'white' }}>Get Quality Certificates</h3>
                <p style={{ color: 'white' }}>Request detailed quality certificates and test reports for our products.</p>
                <a href="/contact-us" className="btn btn-primary">Contact Us</a>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Quality;
